import { experimental_createQueryPersister } from '@tanstack/react-query-persist-client'
import { clear, createStore, del, entries, get, set } from 'idb-keyval'

import { persister, queryClient } from '@shared/providers/src/QueryClientProvider'

const IDBStorage = (idbStore) => ({
  getItem: async (key) => await get(key, idbStore),
  setItem: async (key, value) => await set(key, value, idbStore),
  removeItem: async (key) => await del(key, idbStore),
  entries: async () => await entries(idbStore),
})

const prefix = 'query'
const isIDBAvailable = () => typeof window !== 'undefined' && 'indexedDB' in window
const isPersistingEnabled = () => {
  const cachePersisted = JSON.parse(localStorage.getItem('cache-persisted'))
  const cachePersistedOverride = JSON.parse(localStorage.getItem('cache-persisted-override'))

  return (
    isIDBAvailable() && cachePersistedOverride !== 'not-persisted' && (cachePersistedOverride === 'persisted' || cachePersisted === true)
  )
}

const getStore = () => {
  if (!isIDBAvailable()) return null
  return createStore('careplus', 'react-query-cache')
}

export const clearIDBStore = () => {
  const store = getStore()
  if (!store) return Promise.resolve()
  return clear(store)
}

/**
 * Custom helpers to update both in-memory cache and persisted cache
 * workaround for https://github.com/TanStack/query/issues/6310
 */
export const setQueryData = async (queryKey, updater) => {
  const result = queryClient.setQueryData(queryKey, updater)
  persister?.persistQueryByKey(queryKey, queryClient)
  return result
}

/**
 * Custom helpers to update both in-memory cache and persisted cache
 * workaround for https://github.com/TanStack/query/issues/6310
 */
export const setQueriesData = async (filters, updater, options) => {
  const queries = queryClient.setQueriesData(filters, updater, options)
  queries.forEach(([queryKey]) => {
    persister?.persistQueryByKey(queryKey, queryClient)
  })
  return queries
}

export const getPersister = () => {
  if (!isPersistingEnabled()) return

  return experimental_createQueryPersister({
    storage: IDBStorage(getStore()),
    maxAge: 1000 * 60 * 60 * 8, // 8 hours,
    serialize: (persistedQuery) => persistedQuery,
    deserialize: (cached) => cached,
    prefix,
  })
}
